import { ChannelIcon, useFormat } from '@ly360/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/** Tabs (buttons with aria-selected), used across the retail pages. */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: { key: T; label: string; count?: number }[];
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  const fmt = useFormat();
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={value === t.key}
          onClick={() => onChange(t.key)}
          data-testid={`tab-${t.key}`}
        >
          {t.label}
          {t.count !== undefined && <span className="tab-count">{fmt.number(t.count)}</span>}
        </button>
      ))}
    </div>
  );
}

/** Horizontal bars: one row per item, scaled to the largest value. */
export function BarList({
  rows,
  testId,
}: {
  rows: { key: string; label: ReactNode; value: number; display: string; note?: string }[];
  testId?: string;
}) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="bar-list" data-testid={testId}>
      {rows.map((r) => (
        <li key={r.key}>
          <div className="bar-row">
            <span className="bar-label">{r.label}</span>
            <span className="bar-value">{r.display}</span>
          </div>
          <div className="meter" aria-hidden="true">
            <span style={{ width: `${Math.max(2, (r.value / max) * 100)}%` }} />
          </div>
          {r.note && <div className="faint bar-note">{r.note}</div>}
        </li>
      ))}
    </ul>
  );
}

/** Daily revenue as columns (accessible as a table for screen readers). */
export function DailyChart({
  series,
  currency,
  label,
}: {
  series: { day: string; revenueMinor: string; orders: number }[];
  currency: string;
  label: string;
}) {
  const fmt = useFormat();
  const max = Math.max(1, ...series.map((s) => Number(s.revenueMinor)));
  return (
    <figure className="day-chart" aria-label={label}>
      <div className="day-bars" aria-hidden="true">
        {series.map((s) => {
          const v = Number(s.revenueMinor);
          return (
            <span
              key={s.day}
              className="day-bar"
              style={{ height: `${Math.max(v ? 4 : 1, (v / max) * 100)}%` }}
              title={`${fmt.date(`${s.day}T12:00:00Z`)}: ${fmt.money(v, currency)} · ${s.orders}`}
            />
          );
        })}
      </div>
      <div className="day-axis faint" aria-hidden="true">
        <span>{series[0] && fmt.date(`${series[0].day}T12:00:00Z`)}</span>
        <span>{series.at(-1) && fmt.date(`${series.at(-1)!.day}T12:00:00Z`)}</span>
      </div>
      <table className="sr-only">
        <caption>{label}</caption>
        <tbody>
          {series.map((s) => (
            <tr key={s.day}>
              <th scope="row">{fmt.date(`${s.day}T12:00:00Z`)}</th>
              <td>{fmt.money(Number(s.revenueMinor), currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

const CLASS_TONE: Record<string, string> = {
  vip: 'badge-success',
  loyal: 'badge-success',
  new: 'badge',
  one_time: 'badge-neutral',
  at_risk: 'badge-warning',
  lapsed: 'badge-danger',
  prospect: 'badge-neutral',
};
export function ClassBadge({ value }: { value: string }) {
  const { t } = useTranslation();
  return (
    <span className={`badge ${CLASS_TONE[value] ?? 'badge-neutral'}`} title={t(`retail.classHint.${value}`)}>
      {t(`retail.class.${value}`)}
    </span>
  );
}

/** Where a sale was made: a store, the website, or a chat channel (with its logo). */
export function SourceLabel({ source }: { source: string }) {
  const { t } = useTranslation();
  return (
    <span className="channel-badge">
      <ChannelIcon channel={source} size={12} />
      <span>{t(`retail.source.${source}`)}</span>
    </span>
  );
}

export function PlatformLabel({ platform, short }: { platform: string; short?: boolean }) {
  const { t } = useTranslation();
  return (
    <span className="channel-badge" title={t(`retail.platform.${platform}`)}>
      <ChannelIcon channel={platform} size={12} />
      <span>{t(short ? `retail.platformShort.${platform}` : `retail.platform.${platform}`)}</span>
    </span>
  );
}

/** Percentage change against the previous period, coloured by direction. */
export function Trend({ now, before }: { now: number; before: number }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  if (!before) return <span className="faint">{t('retail.growth.noPrevious')}</span>;
  const change = (now - before) / before;
  const up = change >= 0;
  return (
    <span className={up ? 'trend-up' : 'trend-down'}>
      {up ? '▲' : '▼'} {fmt.number(Math.round(Math.abs(change) * 100))}%{' '}
      <span className="faint">{t('retail.growth.vsPrevious')}</span>
    </span>
  );
}

export function DialogButtons({
  onClose,
  busy,
  label,
  disabled,
}: {
  onClose: () => void;
  busy?: boolean;
  label: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      <button type="button" className="btn btn-ghost" onClick={onClose}>
        {t('common.cancel')}
      </button>
      <button type="submit" className="btn btn-grad" disabled={busy || disabled}>
        {label}
      </button>
    </>
  );
}
